import type {
  Activity,
  AgendaCategory,
  Allocation,
  BoardColumn,
  BudgetMonth,
  CalendarEvent,
  Deployment,
  ErrorGroup,
  Expense,
  Frontier,
  GoldEntry,
  Host,
  Label,
  Member,
  MonitorAlert,
  Occurrence,
  PersonCost,
  ProcessTemplate,
  Project,
  ProjectParticipant,
  ProjectStage,
  PullRequest,
  Repository,
  Role,
  Routine,
  ServiceStatus,
  Ticket,
} from '@sistemas/contracts'


/**
 * Estado do Hub e o reducer puro que o transforma.
 *
 * Tudo o que muda o cache da UI passa por uma ação nomeada. Isso é o que
 * permite testar as transições sem browser, e é o que garante que a mesma
 * mutação aplicada duas vezes seja idempotente (ver `activity/upsert`).
 *
 * O estado é **cache**, não fonte: cada ação aqui só é despachada depois que
 * o banco aceitou a escrita correspondente (ver store.tsx).
 */

export interface ChatMessage {
  readonly id: string
  readonly projectId: string
  readonly authorId: string
  readonly body: string
  readonly activityKeys: readonly string[]
  readonly attachmentName?: string
  readonly createdAt: string
}

export interface AuditEntry {
  readonly id: string
  readonly action: string
  readonly resource: string
  readonly detail: string
  readonly authorId: string
  readonly at: string
}

export interface ActivityEventRow {
  id: string
  activityId: string
  kind: string
  detail: string
  at: string
  /** Quem fez. Ausente só em linhas antigas do cache. */
  authorId?: string
}

/** Papel concedido a um membro. Espelha `sistemas.member_role` da migration 001. */
export interface MemberRoleRow {
  memberId: string
  role: Role
  /** Obrigatório para papéis sensíveis — mesma regra do trigger no banco. */
  reason?: string
  grantedAt: string
}

export interface CoreState {
  projects: Project[]
  /** Colunas do quadro de Projetos, em ordem. */
  projectStages: ProjectStage[]
  /** Quem trabalha ou trabalhou em cada projeto (view `project_participant`). */
  projectParticipants: ProjectParticipant[]
  columns: BoardColumn[]
  activities: Activity[]
  activityEvents: ActivityEventRow[]
  labels: Label[]
  members: Member[]
  memberRoles: MemberRoleRow[]
  costs: PersonCost[]
  allocations: Allocation[]
  processes: ProcessTemplate[]
  messages: ChatMessage[]
  tickets: Ticket[]
  categories: AgendaCategory[]
  routines: Routine[]
  occurrences: Occurrence[]
  events: CalendarEvent[]
  repositories: Repository[]
  pullRequests: PullRequest[]
  deployments: Deployment[]
  hosts: Host[]
  services: ServiceStatus[]
  frontiers: Frontier[]
  errorGroups: ErrorGroup[]
  alerts: MonitorAlert[]
  gold: GoldEntry[]
  budgets: BudgetMonth[]
  expenses: Expense[]
  audit: AuditEntry[]
}

/**
 * Estado de uma carga do banco. Três domínios porque três consultas
 * independentes: C2/C3, C1 e o catálogo C4–C10. Falhar uma não derruba as
 * outras, e cada tela decide qual delas precisa.
 */
export type LoadStatus =
  | { readonly kind: 'carregando' }
  | { readonly kind: 'ok' }
  | { readonly kind: 'erro'; readonly reason: string }

export interface CoreStatus {
  readonly projects: LoadStatus
  readonly people: LoadStatus
  readonly core: LoadStatus
}

export const INITIAL_STATUS: CoreStatus = {
  projects: { kind: 'carregando' },
  people: { kind: 'carregando' },
  core: { kind: 'carregando' },
}

export function initialState(): CoreState {
  return {
    projects: [],
    projectStages: [],
    projectParticipants: [],
    columns: [],
    activities: [],
    activityEvents: [],
    // Etiquetas e processos são catálogo do banco (migration 006). Manter cópia
    // local com id inventado faria a UI oferecer etiqueta que a FK de
    // `activity_label` recusa — erro que só apareceria ao marcar a primeira.
    labels: [],
    members: [],
    memberRoles: [],
    costs: [],
    allocations: [],
    processes: [],
    messages: [],
    tickets: [],
    categories: [],
    routines: [],
    occurrences: [],
    events: [],
    repositories: [],
    pullRequests: [],
    deployments: [],
    hosts: [],
    services: [],
    frontiers: [],
    errorGroups: [],
    alerts: [],
    gold: [],
    budgets: [],
    expenses: [],
    audit: [],
  }
}

export interface ProjectsHydration {
  projects: Project[]
  projectStages: ProjectStage[]
  projectParticipants: ProjectParticipant[]
  columns: BoardColumn[]
  activities: Activity[]
  activityEvents: ActivityEventRow[]
  messages: ChatMessage[]
  labels: Label[]
  processes: ProcessTemplate[]
}

export type CoreAction =
  | { type: 'hydrate/projects'; data: ProjectsHydration }
  | { type: 'hydrate/people'; data: { members: Member[]; memberRoles: MemberRoleRow[] } }
  | { type: 'hydrate/core'; data: Partial<CoreState> }
  // C1
  | { type: 'member/add'; member: Member; roles: MemberRoleRow[] }
  | { type: 'member/upsert'; member: Member }
  | { type: 'memberRoles/replace'; memberId: string; roles: MemberRoleRow[] }
  | { type: 'cost/set'; cost: PersonCost }
  | { type: 'cost/remove'; memberId: string; effectiveFrom: string }
  | {
      type: 'allocation/set'
      memberId: string
      projectId: string
      month: string
      allocation: Allocation | null
    }
  // C2
  | { type: 'project/add'; project: Project; columns: BoardColumn[]; repositoryId?: string }
  | { type: 'project/upsert'; project: Project }
  | { type: 'project/repo'; projectId: string; repositoryId: string | null }
  | { type: 'project/move'; project: Project }
  | { type: 'column/upsert'; column: BoardColumn }
  | { type: 'column/remove'; id: string; targetId: string | null }
  | { type: 'stage/upsert'; stage: ProjectStage }
  | { type: 'stage/remove'; id: string; targetId: string | null }
  | { type: 'project/remove'; id: string }
  | { type: 'activity/add'; activity: Activity; event?: ActivityEventRow }
  | { type: 'activity/upsert'; activity: Activity }
  | { type: 'activities/upsert'; activities: Activity[] }
  | { type: 'activity/remove'; id: string }
  | { type: 'event/push'; event: ActivityEventRow }
  // C3
  | { type: 'message/push'; message: ChatMessage }
  | { type: 'messages/project'; projectId: string; messages: ChatMessage[] }
  // C4–C10
  | { type: 'ticket/patch'; id: string; patch: Partial<Ticket> }
  | { type: 'tickets/replace'; tickets: Ticket[] }
  | { type: 'occurrence/push'; occurrence: Occurrence }
  | { type: 'routine/upsert'; routine: Routine }
  | { type: 'routine/remove'; id: string }
  | { type: 'category/upsert'; category: AgendaCategory }
  | { type: 'event/upsert'; event: CalendarEvent }
  | { type: 'event/remove'; id: string }
  | { type: 'budget/upsert'; budget: BudgetMonth }
  | { type: 'expense/add'; expense: Expense }
  | { type: 'expense/remove'; id: string }
  | { type: 'errorGroup/patch'; id: string; patch: Partial<ErrorGroup> }
  | { type: 'alert/patch'; id: string; patch: Partial<MonitorAlert> }
  | { type: 'gold/push'; entry: GoldEntry }
  | { type: 'audit/push'; entry: AuditEntry }
  | { type: 'reset' }

/** Substitui por id; se não existir, acrescenta. Nunca duplica. */
function upsertById<T extends { id: string }>(list: T[], item: T, append: 'start' | 'end' = 'end'): T[] {
  if (list.some((x) => x.id === item.id)) {
    return list.map((x) => (x.id === item.id ? item : x))
  }
  return append === 'start' ? [item, ...list] : [...list, item]
}

const AUDIT_LIMIT = 500

export function coreReducer(state: CoreState, action: CoreAction): CoreState {
  switch (action.type) {
    case 'hydrate/projects':
      return { ...state, ...action.data }

    case 'hydrate/people':
      return { ...state, members: action.data.members, memberRoles: action.data.memberRoles }

    case 'hydrate/core':
      return { ...state, ...action.data }

    case 'member/add':
      return {
        ...state,
        members: upsertById(state.members, action.member),
        memberRoles: [
          ...state.memberRoles.filter((r) => r.memberId !== action.member.id),
          ...action.roles,
        ],
      }

    case 'member/upsert':
      return { ...state, members: upsertById(state.members, action.member) }

    case 'memberRoles/replace':
      return {
        ...state,
        memberRoles: [
          ...state.memberRoles.filter((r) => r.memberId !== action.memberId),
          ...action.roles,
        ],
      }

    case 'cost/set':
      return {
        ...state,
        costs: [
          ...state.costs.filter(
            (c) =>
              !(c.memberId === action.cost.memberId && c.effectiveFrom === action.cost.effectiveFrom),
          ),
          action.cost,
        ].sort((a, b) => a.effectiveFrom.localeCompare(b.effectiveFrom)),
      }

    case 'cost/remove':
      return {
        ...state,
        costs: state.costs.filter(
          (c) => !(c.memberId === action.memberId && c.effectiveFrom === action.effectiveFrom),
        ),
      }

    case 'allocation/set': {
      const others = state.allocations.filter(
        (a) =>
          !(
            a.memberId === action.memberId &&
            a.projectId === action.projectId &&
            a.month === action.month
          ),
      )
      return { ...state, allocations: action.allocation ? [...others, action.allocation] : others }
    }

    case 'project/add': {
      const repositories = action.repositoryId
        ? linkRepo(state.repositories, action.project.id, action.repositoryId)
        : state.repositories
      return {
        ...state,
        projects: upsertById(state.projects, action.project),
        columns: [
          ...state.columns.filter((c) => c.projectId !== action.project.id),
          ...action.columns,
        ],
        repositories,
      }
    }

    case 'project/upsert':
      return { ...state, projects: upsertById(state.projects, action.project) }

    case 'project/repo':
      return {
        ...state,
        repositories: linkRepo(state.repositories, action.projectId, action.repositoryId),
      }

    // A ordem vem de `boardPosition`, não do índice no array: o quadro ordena
    // ao desenhar. Mover é só trocar a linha pela que o banco devolveu.
    case 'project/move':
      return { ...state, projects: upsertById(state.projects, action.project) }

    case 'column/upsert':
      return { ...state, columns: upsertById(state.columns, action.column) }

    case 'column/remove': {
      // Espelha `delete_board_column`: as atividades vão para o fim do destino,
      // na ordem em que estavam.
      const moving = state.activities
        .filter((a) => a.columnId === action.id)
        .sort((a, b) => a.position - b.position)
      const base = state.activities
        .filter((a) => a.columnId === action.targetId)
        .reduce((max, a) => Math.max(max, a.position), 0)
      const moved = new Map(
        moving.map((a, i) => [
          a.id,
          action.targetId ? { ...a, columnId: action.targetId, position: base + (i + 1) * 1000 } : a,
        ]),
      )
      return {
        ...state,
        columns: state.columns.filter((c) => c.id !== action.id),
        activities: state.activities.map((a) => moved.get(a.id) ?? a),
      }
    }

    case 'stage/upsert': {
      const projectStages = upsertById(state.projectStages, action.stage).sort(
        (a, b) => a.position - b.position,
      )
      // Espelha o trigger `project_stage_cascade_status`: a coluna mudou de
      // fase, os projetos dentro dela mudam junto.
      const projects = state.projects.map((p) =>
        p.stageId === action.stage.id && p.status !== action.stage.status
          ? { ...p, status: action.stage.status }
          : p,
      )
      return { ...state, projectStages, projects }
    }

    case 'stage/remove': {
      // Espelha `delete_project_stage`: os projetos vão para o fim do destino,
      // na ordem em que estavam, e herdam a fase dele.
      const target = state.projectStages.find((st) => st.id === action.targetId)
      const moving = state.projects
        .filter((p) => p.stageId === action.id)
        .sort((a, b) => a.boardPosition - b.boardPosition)
      const base = target
        ? state.projects
            .filter((p) => p.stageId === target.id)
            .reduce((max, p) => Math.max(max, p.boardPosition), 0)
        : 0
      const moved = new Map(
        moving.map((p, i) => [
          p.id,
          target
            ? { ...p, stageId: target.id, status: target.status, boardPosition: base + (i + 1) * 1000 }
            : p,
        ]),
      )
      return {
        ...state,
        projectStages: state.projectStages.filter((st) => st.id !== action.id),
        projects: state.projects.map((p) => moved.get(p.id) ?? p),
      }
    }

    case 'project/remove': {
      // O `on delete cascade` do schema leva colunas, atividades, eventos e
      // mensagens junto — o espelho local replica exatamente isso.
      const columnIds = new Set(
        state.columns.filter((c) => c.projectId === action.id).map((c) => c.id),
      )
      const activityIds = new Set(
        state.activities.filter((a) => a.projectId === action.id).map((a) => a.id),
      )
      return {
        ...state,
        projects: state.projects.filter((p) => p.id !== action.id),
        columns: state.columns.filter((c) => !columnIds.has(c.id)),
        activities: state.activities.filter((a) => !activityIds.has(a.id)),
        activityEvents: state.activityEvents.filter((e) => !activityIds.has(e.activityId)),
        messages: state.messages.filter((m) => m.projectId !== action.id),
        repositories: state.repositories.map((r) => ({
          ...r,
          projectIds: r.projectIds.filter((id) => id !== action.id),
        })),
      }
    }

    case 'activity/add':
      return {
        ...state,
        activities: upsertById(state.activities, action.activity),
        activityEvents: action.event
          ? upsertById(state.activityEvents, action.event, 'start')
          : state.activityEvents,
      }

    case 'activity/upsert':
      return { ...state, activities: upsertById(state.activities, action.activity) }

    case 'activities/upsert': {
      const byId = new Map(action.activities.map((a) => [a.id, a]))
      const known = new Set(state.activities.map((a) => a.id))
      return {
        ...state,
        activities: [
          ...state.activities.map((a) => byId.get(a.id) ?? a),
          ...action.activities.filter((a) => !known.has(a.id)),
        ],
      }
    }

    case 'activity/remove':
      return {
        ...state,
        activities: state.activities.filter((a) => a.id !== action.id),
        activityEvents: state.activityEvents.filter((e) => e.activityId !== action.id),
      }

    case 'event/push':
      return { ...state, activityEvents: upsertById(state.activityEvents, action.event, 'start') }

    case 'messages/project': {
      // Troca só as mensagens deste projeto; as de outros ficam como estão.
      const others = state.messages.filter((m) => m.projectId !== action.projectId)
      return { ...state, messages: [...others, ...action.messages] }
    }

    case 'message/push':
      return { ...state, messages: upsertById(state.messages, action.message) }

    case 'tickets/replace':
      return { ...state, tickets: action.tickets }

    case 'ticket/patch':
      return {
        ...state,
        tickets: state.tickets.map((t) => (t.id === action.id ? { ...t, ...action.patch } : t)),
      }

    case 'occurrence/push':
      return { ...state, occurrences: upsertById(state.occurrences, action.occurrence, 'start') }

    case 'routine/upsert':
      return {
        ...state,
        routines: upsertById(state.routines, action.routine).sort((a, b) =>
          a.name.localeCompare(b.name, 'pt-BR'),
        ),
      }

    case 'routine/remove':
      return { ...state, routines: state.routines.filter((r) => r.id !== action.id) }

    case 'event/upsert':
      return {
        ...state,
        events: upsertById(state.events, action.event).sort(
          (a, b) => a.date.localeCompare(b.date) || (a.time ?? '').localeCompare(b.time ?? ''),
        ),
      }

    case 'event/remove':
      return { ...state, events: state.events.filter((e) => e.id !== action.id) }

    case 'category/upsert':
      return {
        ...state,
        categories: upsertById(state.categories, action.category).sort(
          (a, b) => a.position - b.position || a.name.localeCompare(b.name, 'pt-BR'),
        ),
      }

    case 'budget/upsert': {
      const exists = state.budgets.some((b) => b.month === action.budget.month)
      return {
        ...state,
        budgets: exists
          ? state.budgets.map((b) => (b.month === action.budget.month ? action.budget : b))
          : [...state.budgets, action.budget],
      }
    }

    case 'expense/add':
      return { ...state, expenses: upsertById(state.expenses, action.expense, 'start') }

    case 'expense/remove':
      return { ...state, expenses: state.expenses.filter((e) => e.id !== action.id) }

    case 'errorGroup/patch':
      return {
        ...state,
        errorGroups: state.errorGroups.map((g) =>
          g.id === action.id ? { ...g, ...action.patch } : g,
        ),
      }

    case 'alert/patch':
      return {
        ...state,
        alerts: state.alerts.map((a) => (a.id === action.id ? { ...a, ...action.patch } : a)),
      }

    case 'gold/push':
      return { ...state, gold: upsertById(state.gold, action.entry, 'start') }

    case 'audit/push':
      return { ...state, audit: [action.entry, ...state.audit].slice(0, AUDIT_LIMIT) }

    case 'reset':
      return initialState()

    default:
      return state
  }
}

/** Um projeto tem no máximo um repositório: vincular a um desvincula dos demais. */
function linkRepo(
  repositories: Repository[],
  projectId: string,
  repositoryId: string | null,
): Repository[] {
  return repositories.map((repo) => {
    const without = repo.projectIds.filter((id) => id !== projectId)
    if (repositoryId && repo.id === repositoryId) {
      return { ...repo, projectIds: [...without, projectId] }
    }
    return { ...repo, projectIds: without }
  })
}

/**
 * Marca/desmarca um item de checklist sobre um snapshot da atividade.
 * Usado pelo store depois de serializar as escritas da mesma atividade, para
 * que o segundo clique parta do resultado do primeiro e não de um estado velho.
 */
export function toggleChecklistItemIn(
  activity: Activity,
  checklistId: string,
  itemId: string,
): Activity['checklists'] {
  return activity.checklists.map((c) =>
    c.id !== checklistId
      ? c
      : { ...c, items: c.items.map((i) => (i.id === itemId ? { ...i, done: !i.done } : i)) },
  )
}
